import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useAuth } from '../services/AuthService';
import { usePostHog } from '@posthog/react';
import { getSafeRedirectUrl } from '../utils/redirectUtils';
import {
  buildForgotPasswordUrl,
  buildVerifyUrl,
  formatPhoneNumber,
  isPasswordResetRequiredError,
  isSignInStep,
  isUserNotConfirmedError,
  persistVerifyFlowContext,
  resolvePostAuthDestination,
} from '../utils/authFlowUtils';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = getSafeRedirectUrl(searchParams.get('returnTo'));
  const { signIn, isAuthenticated } = useAuth();
  const posthog = usePostHog();
  const verified = searchParams.get('verified') === '1';

  const [phone, setPhone] = useState(searchParams.get('phone') || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Passwordless flow removed; password-only login

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, navigate, returnTo]);

  // Password-based login
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    const formattedPhone = formatPhoneNumber(phone);

    if (!formattedPhone || formattedPhone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const result = await signIn(formattedPhone, password);
      if (result.success) {
        posthog?.capture('user_logged_in', { method: 'password' });
        const destination = await resolvePostAuthDestination(returnTo);
        navigate(destination, { replace: true });
        return;
      }

      if (isSignInStep(result, 'CONFIRM_SIGN_UP')) {
        persistVerifyFlowContext({
          phone: formattedPhone,
          returnTo,
          next: 'login',
        });
        navigate(buildVerifyUrl({
          phone: formattedPhone,
          returnTo,
          next: 'login',
        }), { replace: true });
        return;
      }

      if (isSignInStep(result, 'RESET_PASSWORD')) {
        navigate(buildForgotPasswordUrl({ phone: formattedPhone }), { replace: true });
        return;
      }

      setError('We need one more verification step before signing you in. Please continue verification or reset your password.');
    } catch (err) {
      if (isUserNotConfirmedError(err)) {
        persistVerifyFlowContext({
          phone: formattedPhone,
          returnTo,
          next: 'login',
        });
        navigate(buildVerifyUrl({
          phone: formattedPhone,
          returnTo,
          next: 'login',
        }), { replace: true });
      } else if (isPasswordResetRequiredError(err)) {
        navigate(buildForgotPasswordUrl({ phone: formattedPhone }), { replace: true });
      } else if (err.name === 'NotAuthorizedException') {
        setError('Incorrect phone number or password');
      } else if (err.name === 'UserNotFoundException') {
        setError('No account found with this phone number');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Passwordless handlers removed

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            Welcome Back
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Sign in to your Labourando account
          </Typography>
        </Box>

        {verified && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Phone verified. Sign in to continue. If you do not remember your password, use Forgot password.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* ===================== PASSWORD MODE ===================== */}
        
          <Box component="form" onSubmit={handlePasswordLogin} noValidate>
            <TextField
              fullWidth
              label="Phone Number"
              placeholder="04XX XXX XXX"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              sx={{ mb: 2.5 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  ),
                },
              }}
              autoComplete="tel"
              autoFocus
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 1 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              autoComplete="current-password"
            />

            <Box sx={{ textAlign: 'right', mb: 3 }}>
              <Link
                component={RouterLink}
                to={buildForgotPasswordUrl({ phone: formatPhoneNumber(phone) || searchParams.get('phone') || '' })}
                variant="body2"
                sx={{ color: 'primary.main' }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !phone || !password}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
                py: 1.5,
                fontWeight: 'bold',
                mb: 2,
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>

            {/* Passwordless / SMS sign-in removed; password-only login retained */}
          </Box>
        
      

        {/* Passwordless flows removed; only password login remains */}

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Don't have an account?{' '}
            <Link
              component={RouterLink}
              to={`/signup${returnTo !== '/' ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`}
              sx={{ color: 'primary.main', fontWeight: 'bold' }}
            >
              Sign Up
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
